import React, {MutableRefObject, useEffect, useMemo, useState} from 'react';
import {Button, ButtonToolbar, Icon, Modal} from '@mc/ui-v4';
import {CatalogLayersSidebar} from './CatalogLayersSidebar';
import {DateFilterItemDefinition, EditorEvent, EditorToolType, FeatureFilter, FeatureId, GroupFeaturePointer, LayerId} from '@mc/react-mapio';
import {FeatureDetailSidebar} from './FeatureDetailSidebar';
import {LayerAttributeVO, TopicVO} from '../../api';
import {mcEditPointCoordinates, mcLayers, mcPrint, mcRules, mcSearch, mcSelect} from '@mc/react-ui-icons';
import classNames from 'classnames';
import {ExportVrstevModal} from '../modals/ExportVrstevModal';
import {ExportObjektuModal} from '../modals/ExportObjektuModal';
import {MeasurementSidebar} from './content/MeasurementSidebar';
import {ETriggerAction, Trigger} from '../../helpers/Trigger';
import {PrintLayersSidebar} from './content/PrintLayersSidebar';
import {SidebarContent} from '../../enums/SidebarContent';
import {useMapioContext} from '../../mapioContext';
import {CoordinatesProps, CoordinatesSidebar} from './content/CoordinatesSidebar';
import {SearchPanel} from '../search-panel/SearchPanel';
import './Sidebar.scss';
import {useAppConfig} from '@mc/react-app-base';
import {useIsMobile} from '../../hooks/useIsMobile';

type Props = {
    backgroundLayer: LayerId;
    coordinateLocation?: CoordinatesProps;
    detailFeatureId: FeatureId;
    detailLayerId: LayerId;
    foregroundLayer: LayerId;
    handleFeatureEditorEvent: (event: EditorEvent) => Promise<void>;
    handleUpdate: (feature: any, layerAttributes: LayerAttributeVO[], initialValues: any) => void;
    catalogMenuVisible: boolean;
    otherToolsTrigger: Trigger;
    setBackgroundLayer: React.Dispatch<React.SetStateAction<LayerId>>;
    setForegroundLayer: React.Dispatch<React.SetStateAction<LayerId>>;
    dateFilterRange: FeatureFilter | null;
    setDateFilterRange: React.Dispatch<React.SetStateAction<FeatureFilter | null>>;
    dateFilterItems: DateFilterItemDefinition[];
    topicChangeEnabled?: boolean;
    optimalScale: any;
    /** Callback pro "Zpět na seznam" v detailu objektu (z panelu hledání) */
    onBackToList?: () => void;
    /** Pointery objektů ve výřezu (s layerId) pro panel hledání (event EXTENT_FEATURES, režim "všechny zapnuté vrstvy"). */
    extentFeaturePointers?: GroupFeaturePointer[];
    /** Ref pro registraci re-aktivace extent filtru (panel hledání) – MapPage ji volá při pohybu mapou. */
    extentRefreshRef: MutableRefObject<(() => void) | null>;
    /** Volá se při výběru položky v panelu hledání – předá funkci pro návrat na seznam. */
    onSearchItemSelected?: (backToListFn: () => void) => void;
    /** Volá se při zavření panelu hledání. */
    onSearchPanelClose?: () => void;
};

const SIDEBAR_SUFFIX = '_SIDEBAR';
/**
 * tyto kódy se tvoří při triggeru measurement toolu díky eventu z angularu (příchozí MeasurementInfo objekt)
 */
const MEASURETOOL_SIDEBAR_ALIASES = [EditorToolType.MEASURE_LINE + SIDEBAR_SUFFIX, EditorToolType.MEASURE_POLYGON + SIDEBAR_SUFFIX];

export const Sidebar: React.FC<Props> = ({
    backgroundLayer,
    coordinateLocation,
    detailFeatureId,
    detailLayerId,
    foregroundLayer,
    handleFeatureEditorEvent,
    handleUpdate,
    catalogMenuVisible,
    otherToolsTrigger,
    setBackgroundLayer,
    setForegroundLayer,
    dateFilterRange,
    setDateFilterRange,
    dateFilterItems,
    topicChangeEnabled,
    optimalScale,
    onBackToList,
    extentFeaturePointers,
    extentRefreshRef,
    onSearchItemSelected,
    onSearchPanelClose,
}) => {
    // @ts-ignore
    const {serverPublic} = useAppConfig();
    const isMobile = useIsMobile();

    const mapioContext = useMapioContext();
    const {activeLayers, activeTool, selectedDetailFeature, mapRef, topic} = mapioContext.value;
    const {setActiveLayers, setSelectedLayerId, setIsClientAttributeTableOpen} = mapioContext;

    const [showSidebarContent, setShowSidebarContent] = useState<SidebarContent | string>(undefined);
    const [collapsedFeatureDetail, setCollapsedFeatureDetail] = useState<FeatureId>(undefined);

    // Panel hledání je po načtení obrazovky defaultně rozbalený (jen jednou; nepřepíše už otevřený obsah,
    // např. detail z hlubokého odkazu). Čekáme na načtení tématu, abychom znali publicSimpleView.
    // Na mobilu je panel trvalý bottom sheet (vykukuje zespodu), proto ho rozbalujeme i tam.
    const didAutoOpenSearchRef = React.useRef(false);
    useEffect(() => {
        if (didAutoOpenSearchRef.current || !serverPublic || !topic?.publicSimpleView) return;
        didAutoOpenSearchRef.current = true;
        setShowSidebarContent(prev => prev ?? SidebarContent.SEARCH_SIDEBAR);
    }, [serverPublic, topic?.publicSimpleView]);

    const handleSetShowSidebarContent = (sideBarContent: SidebarContent | string, ignoreToggle: boolean = false) => {
        if (MEASURETOOL_SIDEBAR_ALIASES.includes(sideBarContent)) {
            setShowSidebarContent(SidebarContent.MEASUREMENT_OVERVIEW_SIDEBAR);
        } else if (sideBarContent === showSidebarContent && !ignoreToggle) {
            setShowSidebarContent(undefined);
        } else {
            setShowSidebarContent(sideBarContent);
        }
    };

    const onClose = (): void => {
        if (
            [SidebarContent.SHOW_COORDS_SIDEBAR, SidebarContent.MEASUREMENT_OVERVIEW_SIDEBAR].includes(showSidebarContent as SidebarContent) &&
            [EditorToolType.SHOW_COORDS, EditorToolType.MEASURE_POLYGON, EditorToolType.MEASURE_LINE].includes(activeTool)
        ) {
            mapRef?.current?.activateToolWithType(EditorToolType.EXACT_SELECT);
        }
        handleSetShowSidebarContent(null);
    };

    const isSelectedDetailFeature: boolean = useMemo<boolean>(() => Boolean(selectedDetailFeature?.length), [selectedDetailFeature]);

    const prevSelectedDetailFeatureKeyRef = React.useRef<string>('');

    useEffect((): void => {
        const key =
            selectedDetailFeature
                ?.map(f => `${f.layerId}_${f.featureId}`)
                .sort()
                .join(',') ?? '';
        const actuallyChanged = key !== prevSelectedDetailFeatureKeyRef.current;
        prevSelectedDetailFeatureKeyRef.current = key;
        if (isSelectedDetailFeature && actuallyChanged) {
            handleSetShowSidebarContent(SidebarContent.FEATURE_DETAIL_SIDEBAR, true);
        }
    }, [selectedDetailFeature]);

    useEffect(() => {
        if (activeTool === EditorToolType.PRINT && showSidebarContent !== SidebarContent.PRINT_SIDEBAR) {
            setShowSidebarContent(SidebarContent.PRINT_SIDEBAR);
        }
        if (coordinateLocation && activeTool === EditorToolType.SHOW_COORDS) {
            setShowSidebarContent(SidebarContent.SHOW_COORDS_SIDEBAR);
        }
    }, [activeTool, coordinateLocation]);

    useEffect((): void => {
        switch (otherToolsTrigger.actionToTrigger) {
            case ETriggerAction.HIDE:
                handleSetShowSidebarContent(undefined, true);
                console.info('Nastavuji otherToolsTrigger.actionToTrigger na', ETriggerAction.NONE, 'mimo set state');
                otherToolsTrigger.actionToTrigger = ETriggerAction.NONE;
                break;
            case ETriggerAction.SHOW:
                if (otherToolsTrigger.tool === EditorToolType.EXPORT_LAYERS) {
                    Modal.show(({onClose}) => <ExportVrstevModal onClose={onClose} />);
                } else if (otherToolsTrigger.tool === EditorToolType.EXPORT_FEATURES) {
                    Modal.show(({onClose}) => <ExportObjektuModal onClose={onClose} />);
                } else {
                    handleSetShowSidebarContent(`${otherToolsTrigger.tool}${SIDEBAR_SUFFIX}`, true);
                }
                console.info('Nastavuji otherToolsTrigger.actionToTrigger na', ETriggerAction.NONE, 'mimo set state');
                otherToolsTrigger.actionToTrigger = ETriggerAction.NONE;
                break;
        }
    }, [otherToolsTrigger]);

    const isSelectedFeatureDetailSidebar: boolean = showSidebarContent === SidebarContent.FEATURE_DETAIL_SIDEBAR;
    const isSelectedCatalogLayersSidebar: boolean = showSidebarContent === SidebarContent.CATALOG_LAYERS_SIDEBAR;
    const isSelectedMeasurementOverviewSidebar: boolean = showSidebarContent === SidebarContent.MEASUREMENT_OVERVIEW_SIDEBAR;
    const isSelectedPrintSidebar: boolean = showSidebarContent === SidebarContent.PRINT_SIDEBAR;
    const isSelectedCoordinatesSidebar: boolean = showSidebarContent === SidebarContent.SHOW_COORDS_SIDEBAR;
    const isSelectedSearchSidebar: boolean = showSidebarContent === SidebarContent.SEARCH_SIDEBAR;

    // Mobilní veřejné zjednodušené Mapio: pravá lišta tlačítek se skrývá (ovládání je nahoře + trvalý sheet),
    // panel hledání (bottom sheet) je vidět vždy, když není otevřený detail / jiný obsah.
    const mobilePublicSimple = isMobile && serverPublic && !!topic?.publicSimpleView;
    // Panel hledání (bottom sheet) je výchozí obsah – vidět VŽDY, když není otevřený žádný jiný obsah
    // (detail objektu, katalog…). Zavření detailu tlačítkem volá handleSetShowSidebarContent(null), proto
    // testujeme falsy (undefined i null), ne jen `=== undefined`.
    // POZOR: klik do prázdné mapy detail zavře přes setSelectedDetailFeature([]) (bez změny showSidebarContent),
    // takže obsah zůstane na FEATURE_DETAIL_SIDEBAR, ačkoli žádný objekt vybraný není – to je „stale detail".
    // V tom případě také zobrazíme hledání, jinak by po kliknutí do mapy zůstala prázdná obrazovka.
    const isStaleDetail = showSidebarContent === SidebarContent.FEATURE_DETAIL_SIDEBAR && !isSelectedDetailFeature;
    const searchPanelActive = mobilePublicSimple ? !showSidebarContent || isSelectedSearchSidebar || isStaleDetail : isSelectedSearchSidebar;
    return (
        <>
            {!mobilePublicSimple && (
            <ButtonToolbar className={`position-absolute sidebar ${isMobile && serverPublic ? 'mobile public' : ''}`}>
                {isSelectedDetailFeature && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {['active']: isSelectedFeatureDetailSidebar})}
                        onClick={(): void => {
                            handleSetShowSidebarContent(SidebarContent.FEATURE_DETAIL_SIDEBAR);
                        }}
                        icon={<Icon icon={mcSelect} intent={'primary'} />}
                    />
                )}

                {[EditorToolType.MEASURE_LINE, EditorToolType.MEASURE_POLYGON].includes(activeTool) && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {['active']: isSelectedMeasurementOverviewSidebar})}
                        onClick={() => {
                            handleSetShowSidebarContent(SidebarContent.MEASUREMENT_OVERVIEW_SIDEBAR);
                        }}
                        icon={<Icon intent={'primary'} icon={mcRules} />}
                    />
                )}
                {activeTool === EditorToolType.PRINT && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {['active']: isSelectedPrintSidebar})}
                        onClick={(): void => {
                            handleSetShowSidebarContent(SidebarContent.PRINT_SIDEBAR);
                        }}
                        icon={<Icon intent={'primary'} icon={mcPrint} />}
                    />
                )}
                {activeTool === EditorToolType.SHOW_COORDS && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {['active']: isSelectedCoordinatesSidebar})}
                        onClick={(): void => {
                            handleSetShowSidebarContent(SidebarContent.SHOW_COORDS_SIDEBAR);
                        }}
                        icon={<Icon intent={'primary'} icon={mcEditPointCoordinates} />}
                    />
                )}

                {serverPublic && topic?.publicSimpleView && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {active: isSelectedSearchSidebar})}
                        onClick={(): void => {
                            handleSetShowSidebarContent(SidebarContent.SEARCH_SIDEBAR);
                        }}
                        icon={<Icon intent={'primary'} icon={mcSearch} />}
                    />
                )}
                {!(serverPublic && topic?.publicSimpleView) && ((catalogMenuVisible && serverPublic) || topic?.showCatalog) && (
                    <Button
                        noFocus
                        inheritColor
                        className={classNames('sidebar-button drop-shadow', {['active']: isSelectedCatalogLayersSidebar})}
                        onClick={(): void => {
                            handleSetShowSidebarContent(SidebarContent.CATALOG_LAYERS_SIDEBAR);
                        }}
                        icon={<Icon intent={'primary'} icon={mcLayers} />}
                    />
                )}
            </ButtonToolbar>
            )}
            {isSelectedDetailFeature && isSelectedFeatureDetailSidebar && (
                <FeatureDetailSidebar
                    collapsedFeatureDetail={collapsedFeatureDetail}
                    layerId={detailLayerId}
                    featureId={detailFeatureId}
                    handleFeatureEditorEvent={handleFeatureEditorEvent}
                    handleUpdate={handleUpdate}
                    onClose={onClose}
                    selectedDetailFeature={selectedDetailFeature}
                    setCollapsedFeatureDetail={setCollapsedFeatureDetail}
                    topic={topic}
                    onBackToList={
                        // „Zpět na seznam" nabízíme VŽDY, když má téma panel hledání – bez ohledu na to, jestli
                        // se do detailu přišlo z panelu, nebo přímo kliknutím do mapy. Vrátí do panelu a odznačí objekt.
                        serverPublic && topic?.publicSimpleView
                            ? (): void => {
                                  mapioContext.setSelectedDetailFeature([]);
                                  handleSetShowSidebarContent(SidebarContent.SEARCH_SIDEBAR, true);
                              }
                            : undefined
                    }
                />
            )}
            {/* SearchPanel renderujeme stále (když je povolený) a jen ho skrýváme přes `active` –
                tím přežije stav hledání i návrat z detailu objektu ("Zpět na seznam"). */}
            {serverPublic && topic?.publicSimpleView && (
                <SearchPanel
                    active={searchPanelActive}
                    extentFeaturePointers={extentFeaturePointers}
                    extentRefreshRef={extentRefreshRef}
                    dateFilterRange={dateFilterRange}
                    onItemSelected={onSearchItemSelected}
                    onClose={(): void => {
                        onClose();
                        onSearchPanelClose?.();
                    }}
                />
            )}
            {isSelectedCatalogLayersSidebar && (
                <CatalogLayersSidebar
                    backgroundLayer={backgroundLayer}
                    foregroundLayer={foregroundLayer}
                    activeLayers={activeLayers}
                    setBackgroundLayer={setBackgroundLayer}
                    setForegroundLayer={setForegroundLayer}
                    setActiveLayers={setActiveLayers}
                    topicChangeEnabled={topicChangeEnabled}
                    showTopicSelector={!serverPublic || topic?.showCatalog}
                    onChange={(activeLayers: string[]): void => {
                        mapRef.current.setLayers(activeLayers);
                    }}
                    onChangeTopic={(value: string): void => {
                        mapioContext.setIsAttributeTableOpen(false);
                        setIsClientAttributeTableOpen(false);
                        setSelectedLayerId('');
                        mapRef.current?.setTopic(value);
                        mapRef.current?.activateToolWithType(EditorToolType.EXACT_SELECT);
                    }}
                    onClickLayerButton={(layerId: LayerId): void => {
                        mapioContext.openAttributeTable(layerId);
                    }}
                    dateFilterRange={dateFilterRange}
                    setDateFilterRange={setDateFilterRange}
                    dateFilterItems={dateFilterItems}
                    onClose={onClose}
                />
            )}
            {isSelectedMeasurementOverviewSidebar &&
                otherToolsTrigger?.toolInfo &&
                [EditorToolType.MEASURE_LINE, EditorToolType.MEASURE_POLYGON].includes(activeTool) && (
                    <MeasurementSidebar onClose={onClose} measurementInfo={otherToolsTrigger?.toolInfo} />
                )}
            {isSelectedPrintSidebar && activeTool === EditorToolType.PRINT && (
                <PrintLayersSidebar onClose={onClose} mapRef={mapRef} optimalScale={optimalScale} />
            )}
            {isSelectedCoordinatesSidebar && coordinateLocation && <CoordinatesSidebar onClose={onClose} coordinateLocation={coordinateLocation} />}
        </>
    );
};
